import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { FetchModule, FetchModuleFailure, FetchModuleSuccess, SetModule } from 'app/library/module/actions';
import { FetchModuleSlack } from 'app/library/module/actions/fetch-module-slack.action';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { Module } from 'app/library/module/models/module.model';
import { ModuleState } from 'app/library/module/models/module.state';
import { ModuleService } from 'app/library/module/services/module.service';
import { FetchMyModule } from 'app/library/my-profile-modules/actions';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { MaxBrainAuthState } from 'app/projects/auth/src/lib/models/auth.state';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { ContentAccessExitService } from 'app/services/content-access-exit.service';
import * as momentImported from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { FeatureService } from '../../../../library/feature/services/feature.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
const moment = momentImported;

@Injectable()
export class ModuleFormGuard extends UnsubscribeOnDestroy {
    @Select(MaxBrainAuthState.getAccessToken)
    accessToken$: Observable<string>;

    moduleId: string;

    public moduleFormSubject = new BehaviorSubject<FormGroup>(null);
    moduleForm$ = this.moduleFormSubject.asObservable();

    /* Preview for module kind template is in parent component */
    /* We need to notify child component to check are there any changes on form to save it    */
    private _checkForChanges = new BehaviorSubject<boolean>(null);
    public checkForChangesSubject$ = this._checkForChanges.asObservable();

    private _saveModuleFormChanges = new BehaviorSubject<boolean>(null);
    public saveModuleFormChangesSubject$ = this._saveModuleFormChanges.asObservable();

    private _discardModuleFormChanges = new BehaviorSubject<boolean>(null);
    public discardModuleFormChangesSubject$ = this._discardModuleFormChanges.asObservable();

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(MyModulesState.getEntities)
    myModuleUser$: Observable<ModuleUser[]>;
    myModuleUsers: ModuleUser[];
    moduleUser: ModuleUser;

    isRedirectedToELearning = false;
    currentModule: Module;

    get moduleMainForm(): FormGroup {
        const moduleForm = this.moduleFormSubject.value;
        return moduleForm && moduleForm.controls.module ? (moduleForm.controls.module as FormGroup) : null;
    }

    get moduleStatusForm(): FormGroup {
        const moduleForm = this.moduleFormSubject.value;
        return moduleForm && moduleForm.controls.status ? (moduleForm.controls.status as FormGroup) : null;
    }

    get moduleContentForm(): FormGroup {
        const moduleForm = this.moduleFormSubject.value;
        return moduleForm && moduleForm.controls.content ? (moduleForm.controls.content as FormGroup) : null;
    }

    private _canUserManage(moduleUserPermission: string): boolean {
        let toReturn = false;

        if (this.profile) {
            toReturn = toReturn || this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        }

        if (this.moduleUser) {
            toReturn = toReturn || this.moduleUser.permissions.includes(moduleUserPermission);
        }

        return toReturn;
    }

    get hasCoursesWritePermission(): boolean {
        return (
            this.profile.permissions.includes(AdminPermissions.COURSES_WRITE) &&
            (this.currentModule.kind === ModuleKind.Execution || this.currentModule.kind === ModuleKind.ProgramExecution || this.currentModule.kind === ModuleKind.ProgramTemplate)
        );
    }

    get hasModulesWritePermission(): boolean {
        return (
            this.profile.permissions.includes(AdminPermissions.USERS_WRITE) && (this.currentModule.kind === ModuleKind.Normal || this.currentModule.kind === ModuleKind.Template)
        );
    }

    get hasTenantAdminPermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    get canUserManageStructure(): boolean {
        return this._canUserManage('module_structure_management');
    }

    get canUserManageCertificate(): boolean {
        return this._canUserManage('module_certificate_management');
    }

    get canUserManageDisplay(): boolean {
        return this._canUserManage('module_display_management');
    }

    get canUserManageEvents(): boolean {
        return this._canUserManage('module_event_management');
    }

    constructor(
        private _store: Store,
        private _actionHelper: NgxsActionHelper,
        private _formBuilder: FormBuilder,
        public moduleService: ModuleService,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _moduleManagersService: ModuleManagersService,
        private _contentAccessExitService: ContentAccessExitService
    ) {
        super();

        this.moduleService.currentModule$.pipe(takeUntil(this._unsubscribeAll)).subscribe((currentModule) => {
            if (currentModule) {
                this.currentModule = currentModule;
                this.createFormGroup();
            }
        });

        this.profile$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((profile) => !!profile)
            )
            .subscribe((profile) => {
                this.profile = profile;
                this.createFormGroup();
            });

        this.myModuleUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((myModuleUsers) => !!myModuleUsers)
            )
            .subscribe((myModuleUsers) => {
                this.myModuleUsers = myModuleUsers;
                this.createFormGroup();
            });
    }

    checkForChanges(value: boolean): void {
        this._checkForChanges.next(value);
    }

    saveModuleFormChanges(value: boolean): void {
        this._saveModuleFormChanges.next(value);
    }

    discardModuleFormChanges(value: boolean): void {
        this._discardModuleFormChanges.next(value);
    }

    createFormGroup(): boolean {
        if (!this.profile) {
            return false;
        }

        const currentModule = this.moduleService.getCurrentModule();

        if (!currentModule) {
            return false;
        }

        if (this.myModuleUsers) {
            this.moduleUser = this.myModuleUsers.find((tempModuleUser) => tempModuleUser.moduleId === currentModule.id);
        }

        const setDurationManually = !!currentModule.digicompDuration;
        const isDisabled = !this.canUserManageStructure && !this.hasCoursesWritePermission && !this.hasModulesWritePermission && !this.hasTenantAdminPermission;

        this.moduleFormSubject.next(
            this._formBuilder.group({
                module: this._formBuilder.group({
                    id: [currentModule.id],
                    kind: [currentModule.kind],
                    isPrivate: [{ value: currentModule.isPrivate, disabled: isDisabled }, Validators.required],
                    defaultPinned: [{ value: currentModule.defaultPinned, disabled: isDisabled }],
                    collapseGeneralInfo: [{ value: currentModule.collapseGeneralInfo, disabled: isDisabled }],
                    completionRule: [currentModule.completionRule],
                    name: [
                        {
                            value: currentModule.name,
                            disabled: isDisabled,
                        },
                        Validators.required,
                    ],
                    version: [{ value: currentModule.version, disabled: isDisabled }],
                    description: [currentModule.description || ''],
                    startDate: [{ value: currentModule.startDate || null, disabled: isDisabled }, currentModule.selfPaced ? null : Validators.required],
                    endDate: [{ value: currentModule.endDate || null, disabled: isDisabled }, currentModule.selfPaced ? null : Validators.required],
                    dueDate: [{ value: currentModule.dueDate || null, disabled: isDisabled }],
                    setDurationManually: [{ value: setDurationManually, disabled: isDisabled }],
                    digicompDuration: this._formBuilder.group({
                        y: [currentModule.digicompDuration ? currentModule.digicompDuration.y : 0],
                        m: [currentModule.digicompDuration ? currentModule.digicompDuration.m : 0],
                        w: [currentModule.digicompDuration ? currentModule.digicompDuration.w : 0],
                        d: [currentModule.digicompDuration ? currentModule.digicompDuration.d : 0],
                        hh: [currentModule.digicompDuration ? currentModule.digicompDuration.hh : 0],
                        mm: [currentModule.digicompDuration ? currentModule.digicompDuration.mm : 0],
                    }),
                    duration: [{ value: currentModule.durationTime, disabled: isDisabled }],
                    tags: [{ value: currentModule.tags, disabled: isDisabled }],
                    slackChannel: [currentModule.slackChannel],
                }),
                status: this._formBuilder.group({
                    id: [{ value: currentModule.id, disabled: isDisabled }, Validators.required],
                    status: [{ value: currentModule.status, disabled: isDisabled }, Validators.required],
                }),
                content: this._formBuilder.group({
                    id: [currentModule.id, Validators.required],
                    structure: [{ value: currentModule.hasSections, disabled: false }, Validators.required],
                    rule: [{ value: currentModule.sectionRule, disabled: !currentModule.hasSections }],
                }),
            })
        );

        return true;
    }

    async checkIfModulePublished(moduleId: string): Promise<void> {
        const currentModule = this.moduleService.getCurrentModule();
        let moduleManagers = this._moduleManagersService.getModuleManagers();

        if (!moduleManagers) {
            await this._moduleManagersService.fetchManagersByModuleId(moduleId);
            moduleManagers = this._moduleManagersService.getModuleManagers();
        }

        if (
            !this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT) &&
            !moduleManagers?.filter((manager) => manager.userId === this.profile.id).length &&
            currentModule.status === 'unpublished'
        ) {
            this._contentAccessExitService.handleUnavailableContent();
        }
    }

    async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        this.moduleId = next.params['id'];

        const actions = [];

        const moduleToSet = ModuleState.getEntity(this._store.selectSnapshot(ModuleState), this.moduleId);

        await this._moduleManagersService.fetchManagersByModuleId(this.moduleId);

        const moduleManagers = this._moduleManagersService.getModuleManagers();

        const hasCoursesWritePermission = this.profile.permissions.includes(AdminPermissions.COURSES_WRITE);
        const hasModulesWritePermission = this.profile.permissions.includes(AdminPermissions.USERS_WRITE);
        const hasCoursesReadPermission = this.profile.permissions.includes(AdminPermissions.COURSES_READ);
        const hasModulesReadPermission = this.profile.permissions.includes(AdminPermissions.MODULES_READ);
        const isManager = moduleManagers?.some((manager) => manager.userId === this.profile.id);
        const isTenantAdmin = this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);

        // Redirect if not manager and not course writer
        if (!isManager && !isTenantAdmin && !hasCoursesWritePermission && !hasModulesWritePermission && !hasCoursesReadPermission && !hasModulesReadPermission) {
            const routes = state.url.split('/');
            routes.splice(3, 1);
            this._store.dispatch(new Navigate([...routes]));
            return false;
        }

        const redirect = (module: Module) => {
            const routes = state.url.split('/');
            routes.splice(0, 1); // remove first item because it is an empty string

            const editIndex = routes.indexOf('edit');

            if (routes[routes.length - 1] === 'edit') {
                switch (module.type) {
                    case 'e_learning':
                        routes.push('e-learning');
                        break;
                    default:
                        break;
                }

                this._store.dispatch(new Navigate(routes));
            } else {
                const newRoutes = routes.slice(0, editIndex + 1);
                switch (routes[editIndex + 1]) {
                    case 'e-learning':
                        if (module.type !== 'e_learning') {
                            this._store.dispatch(new Navigate([...newRoutes]));
                        }
                        break;
                    default:
                        if (module.type === 'e_learning') {
                            this.isRedirectedToELearning = true;
                            this._store.dispatch(new Navigate([...newRoutes, 'e-learning']));
                        }
                        break;
                }
            }
        };

        if (moduleToSet) {
            if (
                !moduleToSet.slackChannel &&
                !this.isRedirectedToELearning &&
                this._featureService.checkFeatureStatus(FeatureSwitchName.Slack) &&
                moduleToSet.kind !== ModuleKind.Template
            ) {
                this._store.dispatch(new FetchModuleSlack({ moduleId: moduleToSet.id }));
            }

            this.isRedirectedToELearning = false;

            setTimeout(() => {
                this._store.dispatch(new SetModule(moduleToSet));
            });
        }

        actions.push(new FetchModule(this.moduleId));

        if (state.url.includes('edit/participants')) {
            actions.push(new FetchMyModule({ moduleId: this.moduleId, includeAnalytics: true }));
        } else {
            actions.push(new FetchMyModule({ moduleId: this.moduleId }));
        }

        this.accessToken$
            .pipe(
                filter((accessToken) => !!accessToken),
                take(1)
            )
            .subscribe(() => {
                this._store.dispatch(actions);
            });

        return this._actionHelper
            .race(FetchModuleSuccess, FetchModuleFailure)
            .pipe(
                tap((success) => {
                    if (!success) {
                        this._contentAccessExitService.handleUnavailableContent();
                    } else {
                        this.checkIfModulePublished(this.moduleId);

                        redirect(this._store.selectSnapshot(ModuleState.getEntity));
                    }
                })
            )
            .toPromise();
    }
}
